/**
 * Inline FDI chart under Chief Complaint (Figma: "Dental · Inline chart +
 * treatment plan (draft)", node 7147:4508).
 *
 * Tap teeth to select one or several, then toggle a condition or mobility
 * grade: it applies to every selected tooth. Collapses to summary chips.
 * Controlled: the page owns the findings and the dentition.
 */
import React, { useMemo, useState } from "react";
import {
  CONDITION_BY_KEY,
  DENTAL_CONDITIONS,
  type ConditionKey,
} from "../../constants/dental.constants";
import type { DentalChart, DentalFinding } from "../../schemas/dental.schema";

type Dentition = DentalChart["dentition"];

const ARCH: Record<"permanent" | "primary", { ur: number[]; ul: number[]; lr: number[]; ll: number[] }> = {
  permanent: {
    ur: [18, 17, 16, 15, 14, 13, 12, 11], ul: [21, 22, 23, 24, 25, 26, 27, 28],
    lr: [48, 47, 46, 45, 44, 43, 42, 41], ll: [31, 32, 33, 34, 35, 36, 37, 38],
  },
  primary: {
    ur: [55, 54, 53, 52, 51], ul: [61, 62, 63, 64, 65],
    lr: [85, 84, 83, 82, 81], ll: [71, 72, 73, 74, 75],
  },
};

const QUADRANT = ["", "Upper right", "Upper left", "Lower left", "Lower right"];
const PERM_POS = ["", "central incisor", "lateral incisor", "canine", "first premolar", "second premolar", "first molar", "second molar", "third molar"];
const PRIM_POS = ["", "central incisor", "lateral incisor", "canine", "first molar", "second molar"];
const ROMAN = ["", "I", "II", "III"];

export function toothName(fdi: number): string {
  const q = Math.floor(fdi / 10);
  const primary = q >= 5;
  return `${QUADRANT[((q - 1) % 4) + 1]} ${(primary ? PRIM_POS : PERM_POS)[fdi % 10]}`;
}

const isCharted = (f?: DentalFinding) => !!f && (f.conditions.length > 0 || !!f.mobility_grade);

interface Props {
  findings: DentalFinding[];
  onChange: (findings: DentalFinding[]) => void;
  dentition: Dentition;
  onDentitionChange: (d: Dentition) => void;
}

export const DentalChartPanel: React.FC<Props> = ({ findings, onChange, dentition, onDentitionChange }) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const byTooth = useMemo(() => new Map(findings.map((f) => [f.tooth, f])), [findings]);
  const charted = findings.filter(isCharted).sort((a, b) => a.tooth - b.tooth);
  // A mixed chart shows the adult arch; milk teeth already charted stay listed in the chips.
  const arch = ARCH[dentition === "primary" ? "primary" : "permanent"];

  const write = (teeth: number[], edit: (f: DentalFinding) => DentalFinding) => {
    const next = new Map(byTooth);
    for (const t of teeth) next.set(t, edit(next.get(t) ?? { tooth: t, conditions: [], mobility_grade: null }));
    onChange([...next.values()].filter(isCharted).sort((a, b) => a.tooth - b.tooth));
  };

  const allHave = (key: ConditionKey) => selected.length > 0 && selected.every((t) => byTooth.get(t)?.conditions.includes(key));
  const toggleCondition = (key: ConditionKey) => {
    const remove = allHave(key);
    write(selected, (f) => ({
      ...f,
      conditions: remove ? f.conditions.filter((c) => c !== key) : f.conditions.includes(key) ? f.conditions : [...f.conditions, key],
    }));
  };
  const mobilityOf = (t: number) => byTooth.get(t)?.mobility_grade ?? null;
  const allMobility = (g: 1 | 2 | 3) => selected.length > 0 && selected.every((t) => mobilityOf(t) === g);
  const toggleMobility = (g: 1 | 2 | 3) => {
    const clear = allMobility(g);
    write(selected, (f) => ({ ...f, mobility_grade: clear ? null : g }));
  };
  const clearSelected = () => {
    onChange(findings.filter((f) => !selected.includes(f.tooth)));
    setSelected([]);
  };
  const toggleTooth = (t: number) => setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const chips = (
    <div className="flex flex-wrap gap-1.5">
      {charted.map((f) => (
        <button
          key={f.tooth}
          type="button"
          onClick={() => { setCollapsed(false); setSelected([f.tooth]); }}
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-200"
        >
          {f.conditions.map((c) => (
            <span key={c} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: CONDITION_BY_KEY[c]?.color }} />
          ))}
          <span className="font-semibold tabular-nums">{f.tooth}</span>
          <span>
            {[...f.conditions.map((c) => CONDITION_BY_KEY[c]?.label ?? c), f.mobility_grade ? `Mobility ${ROMAN[f.mobility_grade]}` : null]
              .filter(Boolean).join(", ")}
          </span>
        </button>
      ))}
    </div>
  );

  const tooth = (t: number, upper: boolean) => {
    const f = byTooth.get(t);
    const has = isCharted(f);
    const sel = selected.includes(t);
    const crown = (
      <span
        className={`block h-[21px] w-[17px] border-[1.3px] ${has ? "border-blue-500 bg-blue-50" : "border-slate-400"} ${upper ? "rounded-t-[3px] rounded-b-[9px]" : "rounded-t-[9px] rounded-b-[3px]"}`}
      />
    );
    const marks = (
      <span className="flex h-[7px] items-center gap-0.5">
        {f?.conditions.map((c) => (
          <span key={c} className="h-[5px] w-[5px] rounded-full" style={{ backgroundColor: CONDITION_BY_KEY[c]?.color }} />
        ))}
        {f?.mobility_grade && (
          <span className="rounded-[3px] bg-red-500 px-0.5 text-[7px] font-bold leading-[9px] text-white">M{f.mobility_grade}</span>
        )}
      </span>
    );
    const num = <span className={`text-[10px] tabular-nums ${has ? "font-bold text-blue-700" : "text-slate-500"}`}>{t}</span>;
    return (
      <button
        key={t}
        type="button"
        aria-pressed={sel}
        aria-label={`Tooth ${t}, ${toothName(t)}${has ? ", charted" : ""}`}
        onClick={() => toggleTooth(t)}
        className={`flex w-[26px] flex-col items-center gap-0.5 rounded-md border-[1.5px] py-1 ${sel ? "border-blue-300 bg-blue-50" : "border-transparent hover:bg-slate-50"}`}
      >
        {upper ? <>{crown}{marks}{num}</> : <>{num}{marks}{crown}</>}
      </button>
    );
  };

  const jaw = (left: number[], right: number[], upper: boolean) => (
    <div className="flex justify-center gap-1.5">
      <div className="flex gap-0.5">{left.map((t) => tooth(t, upper))}</div>
      <div className="w-px bg-slate-300" />
      <div className="flex gap-0.5">{right.map((t) => tooth(t, upper))}</div>
    </div>
  );

  const label = (l: string, r: string) => (
    <div className="flex justify-between px-2 text-[10.5px] text-slate-500"><span>{l}</span><span>{r}</span></div>
  );

  const single = selected.length === 1 ? selected[0] : null;

  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[15px] font-bold text-slate-800">Dental Chart</h2>
        {collapsed && charted.length > 0 && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
            {charted.length} {charted.length === 1 ? "tooth" : "teeth"} charted
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {!collapsed && (
            <div className="flex rounded-lg bg-slate-100 p-0.5" role="group" aria-label="Dentition">
              {(["permanent", "primary"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={dentition === d}
                  onClick={() => { setSelected([]); onDentitionChange(d); }}
                  className={`rounded-md px-2.5 py-1 text-xs ${dentition === d ? "bg-white font-semibold text-slate-800 shadow-sm" : "text-slate-500"}`}
                >
                  {d === "permanent" ? "Adult" : "Child"}
                </button>
              ))}
            </div>
          )}
          <button type="button" onClick={() => setCollapsed((c) => !c)} className="text-xs font-semibold text-sky-700">
            {collapsed ? "Expand" : "Collapse"}
          </button>
        </div>
      </div>

      {collapsed ? (
        charted.length > 0 ? chips : <p className="text-xs text-slate-500">No teeth charted.</p>
      ) : (
        <>
          <p className="text-xs text-slate-500">Tap teeth to select one or several, then pick a condition.</p>
          <div className="overflow-x-auto">
            <div className="flex min-w-[470px] flex-col gap-1 rounded-xl border border-slate-200 bg-white p-3">
              {dentition === "primary"
                ? label("Upper right 55 – 51", "61 – 65 Upper left")
                : label("Upper right 18 – 11", "21 – 28 Upper left")}
              {jaw(arch.ur, arch.ul, true)}
              <div className="mx-1.5 my-0.5 h-px bg-slate-200" />
              {jaw(arch.lr, arch.ll, false)}
              {dentition === "primary"
                ? label("Lower right 85 – 81", "71 – 75 Lower left")
                : label("Lower right 48 – 41", "31 – 38 Lower left")}
            </div>
          </div>

          {selected.length > 0 && (
            <div className="flex flex-col gap-3 rounded-xl border border-blue-300 bg-white p-3.5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    {single ? `Tooth ${single}` : `${selected.length} teeth: ${[...selected].sort((a, b) => a - b).join(", ")}`}
                  </h3>
                  <p className="text-xs text-sky-700">{single ? toothName(single) : "Changes apply to every selected tooth"}</p>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={clearSelected} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>
                  <button type="button" onClick={() => setSelected([])} className="text-xs font-semibold text-sky-700">Done</button>
                </div>
              </div>
              {(["disease", "state"] as const).map((group) => (
                <div key={group} className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {group === "disease" ? "Disease" : "Existing state"}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {DENTAL_CONDITIONS.filter((c) => c.group === group).map((c) => {
                      const on = allHave(c.key);
                      return (
                        <button
                          key={c.key}
                          type="button"
                          aria-pressed={on}
                          onClick={() => toggleCondition(c.key)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${on ? "border-slate-700 bg-slate-50 font-semibold text-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                        >
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600">Mobility</span>
                {([1, 2, 3] as const).map((g) => (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={allMobility(g)}
                    onClick={() => toggleMobility(g)}
                    className={`h-7 w-9 rounded-md border text-xs ${allMobility(g) ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-600"}`}
                  >
                    {ROMAN[g]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {charted.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-slate-500">Charted</span>
              {chips}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DentalChartPanel;
